// src/hooks/useInViewport.js
import { useEffect, useRef, useState } from "react";

/**
 * Returns a ref to attach to an element and a boolean indicating
 * whether that element has entered the viewport at least once.
 * Uses IntersectionObserver with a rootMargin so content starts
 * loading slightly before it's actually visible (avoids pop-in).
 */
const useInViewport = ({ rootMargin = "200px", once = true } = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || (once && isInView)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setIsInView(false);
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin, once, isInView]);

  return [ref, isInView];
};

export default useInViewport;